import { useEffect, useRef, useState } from 'react';
import { gql, useApolloClient } from '@apollo/client';

const DEALER_LOCATIONS_QUERY = gql`
  query integrationSettingsDealerLocations($accountId: ID) {
    publicAccount(id: $accountId) {
      id
      locations {
        locationId
        locationName
        childrenLocation {
          locationId
          locationName
        }
      }
    }
  }
`;

export interface DealerLocationOption {
  locationId: string;
  locationName: string;
}

interface DealerLocationNode extends DealerLocationOption {
  childrenLocation?: DealerLocationOption[] | null;
}

interface DealerLocationsResponse {
  publicAccount?: { locations?: DealerLocationNode[] | null } | null;
}

const flattenLocations = (locations: DealerLocationNode[]) =>
  locations.reduce<DealerLocationOption[]>((acc, location) => {
    acc.push({ locationId: location.locationId, locationName: location.locationName });
    (location.childrenLocation || []).forEach(child =>
      acc.push({ locationId: child.locationId, locationName: child.locationName })
    );
    return acc;
  }, []);

export const useDealerLocations = (accountIds: string[]) => {
  const client = useApolloClient();
  const [locationsByAccountId, setLocationsByAccountId] = useState<Record<string, DealerLocationOption[]>>({});
  const requestedIds = useRef(new Set<string>());
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const idsKey = accountIds.join(',');

  useEffect(() => {
    const missingIds = accountIds.filter(id => id && !requestedIds.current.has(id));

    missingIds.forEach(id => {
      requestedIds.current.add(id);

      client
        .query<DealerLocationsResponse>({
          query: DEALER_LOCATIONS_QUERY,
          variables: { accountId: id },
          fetchPolicy: 'no-cache',
          context: { authRequired: true },
        })
        .then(({ data }) => flattenLocations(data?.publicAccount?.locations || []))
        .catch(() => [] as DealerLocationOption[])
        .then(locations => {
          if (isMounted.current) setLocationsByAccountId(prev => ({ ...prev, [id]: locations }));
        });
    });
  }, [idsKey, client]);

  const isLoading = accountIds.some(id => id && !(id in locationsByAccountId));

  return { locationsByAccountId, isLoading };
};
